import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Header from './components/ui/Header';
import Footer from './components/ui/Footer';
import Controls from './components/Controls/Controls';
import SummaryView from './components/Visualization/SummaryView';
import ParallelCoordinatePlot from './components/Visualization/ParallelCoordinatePlot';
import { HierarchyNavigator } from './components/navigation/HierarchyNavigator';
import { datasets } from './data/sampleData';
import { createHierarchy } from './utils/projection';
import { Dataset, HierarchyState, Brushable } from './types';
import './styles/index.css';
import './styles/components.css';
import './styles/layout.css';
import './styles/visualization.css';

// Import the UI components directly
const LoadingIndicator = require('./components/ui/LoadingIndicator').default;
const ErrorBoundary = require('./components/ui/ErrorBoundary').default;

const App: React.FC = () => {
  // Refs
  const appRef = useRef<HTMLDivElement>(null);

  // Dataset state
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>('weatherData');
  const [selectedDataset, setSelectedDataset] = useState<Dataset>(datasets[selectedDatasetId]);

  // Hierarchy state
  const [hierarchyState, setHierarchyState] = useState<HierarchyState>({
    levels: {},
    currentLevelId: '',
    path: [],
    selectedGroups: {},
  });

  // Visualization state
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [brushes, setBrushes] = useState<Record<string, Brushable>>({});
  const [dimensions, setDimensions] = useState<any[]>([]);
  const [showLabels, setShowLabels] = useState<boolean>(true);

  // UI state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Responsive container dimensions
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });

  // Get current level and selected groups
  const currentLevel = hierarchyState.levels[hierarchyState.currentLevelId] || null;
  const selectedGroupIds = useMemo(
    () => hierarchyState.selectedGroups[hierarchyState.currentLevelId] || [],
    [hierarchyState.selectedGroups, hierarchyState.currentLevelId],
  );
  const selectedGroups = useMemo(
    () => currentLevel?.dimensionGroups.filter((g) => selectedGroupIds.includes(g.id)) || [],
    [currentLevel, selectedGroupIds],
  );

  // Debug logging
  useEffect(() => {
    console.log('Current level:', currentLevel);
    console.log('Selected group IDs:', selectedGroupIds);
    console.log('Selected groups:', selectedGroups);
    console.log('Container dimensions:', containerDimensions);
  }, [currentLevel, selectedGroupIds, selectedGroups, containerDimensions]);

  // Initialize hierarchy when dataset changes
  useEffect(() => {
    if (selectedDataset) {
      try {
        setIsLoading(true);
        const hierarchy = createHierarchy(selectedDataset);
        setHierarchyState({
          levels: hierarchy,
          currentLevelId: 'level-0',
          path: ['level-0'],
          selectedGroups: { 'level-0': ['all-dimensions'] },
        });
        setError(null);
      } catch (err) {
        console.error('Error creating hierarchy:', err);
        setError('Failed to process dataset hierarchy. Please try a different dataset.');
      } finally {
        setIsLoading(false);
      }
    }
  }, [selectedDataset]);

  // Update selected dataset when ID changes
  useEffect(() => {
    try {
      setIsLoading(true);
      setSelectedDataset(datasets[selectedDatasetId]);
    } catch (err) {
      console.error('Error setting dataset:', err);
      setError(`Failed to load dataset: ${selectedDatasetId}`);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDatasetId]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const visualizationContainer = document.querySelector('.visualization-container');
      if (visualizationContainer) {
        const { width, height } = visualizationContainer.getBoundingClientRect();
        console.log('Container dimensions:', { width, height });
        setContainerDimensions({
          width: Math.max(600, width), // Ensure minimum width
          height: Math.max(500, height), // Ensure minimum height
        });
      } else {
        console.error('Visualization container not found in DOM');
        // Set default dimensions if container not found
        setContainerDimensions({
          width: 800,
          height: 500,
        });
      }
    };

    // Add small delay to ensure container is rendered
    setTimeout(handleResize, 100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update dimensions based on selected groups
  useEffect(() => {
    if (selectedDataset && selectedGroups.length > 0) {
      // Extract dimensions from selected groups
      const dimensionIds = Array.from(new Set(selectedGroups.flatMap((group) => group.dimensions)));

      const pcpDimensions = dimensionIds
        .map((id) => selectedDataset.dimensions.find((d) => d.id === id))
        .filter((d) => d !== undefined);

      console.log('Selected dimensions:', pcpDimensions);
      setDimensions(pcpDimensions as any[]);
    } else {
      console.log('No dimensions selected');
      setDimensions([]);
    }
  }, [selectedDataset, selectedGroups]);

  // Handle dataset change
  const handleDatasetChange = (datasetId: string) => {
    setSelectedDatasetId(datasetId);
    // Reset other states when changing dataset
    setSelectedItems([]);
    setBrushes({});
  };

  // Handle hierarchy navigation
  const handleLevelChange = useCallback((levelId: string) => {
    setHierarchyState((prev) => ({
      ...prev,
      currentLevelId: levelId,
      path: [...prev.path.slice(0, prev.path.indexOf(levelId) + 1)],
    }));
  }, []);

  const handleDrillDown = (groupId: string) => {
    const currentLevel = hierarchyState.levels[hierarchyState.currentLevelId];
    if (currentLevel && currentLevel.childLevels && currentLevel.childLevels.length > 0) {
      const nextLevelId = currentLevel.childLevels[0];
      setHierarchyState((prev) => ({
        ...prev,
        currentLevelId: nextLevelId,
        path: [...prev.path, nextLevelId],
        selectedGroups: {
          ...prev.selectedGroups,
          [nextLevelId]: [groupId],
        },
      }));
    }
  };

  const handleDrillUp = useCallback(() => {
    if (hierarchyState.path.length > 1) {
      const parentLevelId = hierarchyState.path[hierarchyState.path.length - 2];
      setHierarchyState((prev) => ({
        ...prev,
        currentLevelId: parentLevelId,
        path: prev.path.slice(0, -1),
      }));
    }
  }, [hierarchyState.path]);

  // Toggle axis labels
  const handleToggleLabels = useCallback(() => {
    setShowLabels((prev) => !prev);
  }, []);

  // Reset view
  const handleResetView = useCallback(() => {
    setBrushes({});
    setSelectedItems([]);
    // Reset to initial level if nested
    if (hierarchyState.path.length > 1) {
      setHierarchyState((prev) => ({
        ...prev,
        currentLevelId: 'level-0',
        path: ['level-0'],
        selectedGroups: { 'level-0': ['all-dimensions'] },
      }));
    }
  }, [hierarchyState.path.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle keyboard events when the app is focused, not during input
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'SELECT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      switch (event.key) {
        case 'Escape':
          // Reset selection
          setSelectedItems([]);
          setBrushes({});
          break;
        case ' ':
        case 'l':
        case 'L':
          // Toggle labels
          event.preventDefault(); // Prevent scrolling with space
          handleToggleLabels();
          break;
        case 'r':
        case 'R':
          // Reset view
          handleResetView();
          break;
        case 'ArrowUp':
          // Navigate up
          event.preventDefault();
          handleDrillUp();
          break;
        case 'ArrowDown':
          // Navigate down
          event.preventDefault();
          const currentLevel = hierarchyState.levels[hierarchyState.currentLevelId];
          if (currentLevel?.childLevels && currentLevel.childLevels.length > 0) {
            handleLevelChange(currentLevel.childLevels[0]);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    handleToggleLabels,
    handleResetView,
    handleDrillUp,
    handleLevelChange,
    hierarchyState,
    setSelectedItems,
    setBrushes,
  ]);

  return (
    <div className="app" ref={appRef} tabIndex={0}>
      <Header githubUrl="https://github.com/yourusername/multiscale-pcp" />

      <main className="main">
        <ErrorBoundary>
          <div className="container">
            <Controls
              datasets={Object.values(datasets)}
              selectedDataset={selectedDatasetId}
              onSelectDataset={handleDatasetChange}
              hierarchyState={hierarchyState}
              onLevelChange={handleLevelChange}
              onDrillDown={handleDrillDown}
              onDrillUp={handleDrillUp}
              onToggleLabels={handleToggleLabels}
              onResetView={handleResetView}
              showLabels={showLabels}
            />

            {error && (
              <div className="error-message" role="alert">
                <span className="error-icon">⚠️</span> {error}
              </div>
            )}

            {isLoading ? (
              <LoadingIndicator />
            ) : (
              currentLevel && (
                <>
                  <HierarchyNavigator
                    path={hierarchyState.path}
                    levels={hierarchyState.levels}
                    onNavigate={handleLevelChange}
                  />

                  <div className="summary-container">
                    <SummaryView
                      dataset={selectedDataset}
                      dimensionGroups={selectedGroups}
                      selectedGroups={selectedGroupIds}
                      onSelect={(groups) => {
                        setHierarchyState((prev) => ({
                          ...prev,
                          selectedGroups: {
                            ...prev.selectedGroups,
                            [prev.currentLevelId]: groups,
                          },
                        }));
                      }}
                      onDrillDown={handleDrillDown}
                      width={containerDimensions.width}
                      height={200}
                      // @ts-ignore - showLabels is defined in SummaryViewProps
                      showLabels={showLabels}
                    />
                  </div>

                  <div className="visualization-container">
                    {/* Add debug info */}
                    <div className="debug-info" style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                      Width: {containerDimensions.width}, Height: {containerDimensions.height}, Groups:{' '}
                      {selectedGroups.length}, Dimensions: {dimensions.length}
                    </div>

                    <ParallelCoordinatePlot
                      dataset={selectedDataset}
                      selectedGroups={selectedGroups}
                      width={containerDimensions.width}
                      height={containerDimensions.height}
                      selectedItems={selectedItems}
                      brushes={brushes}
                      onBrush={setBrushes}
                      onSelect={setSelectedItems}
                      // @ts-ignore - showLabels exists in the component but TypeScript doesn't see it
                      showLabels={showLabels}
                    />
                  </div>
                </>
              )
            )}
          </div>
        </ErrorBoundary>
      </main>

      <Footer version="1.0.1" />
    </div>
  );
};

export default App;
